import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});


/**
 * Calculate processing fees with backend API
 * @param {number} totalAmount - Total booking amount
 * @param {string} currency - Currency code (e.g., 'USD', 'GBP', 'EUR')
 * @returns {Promise<Object>} Processing fees breakdown
 */
export const calculateProcessingFees = async (totalAmount, currency) => {
    try {
        const response = await api.post('/experiences/calculate-processing-fees', {
            totalAmount,
            currency
        });

        if (response.data.success) {
            return {
                success: true,
                ...response.data.data
            };
        } else {
            throw new Error(response.data.message || 'Failed to calculate processing fees');
        }
    } catch (error) {
        console.error('[ProcessingFeesService] API call failed:', {
            error: error.message,
            response: error.response?.data
        });

        // Return fallback calculation if API fails
        return {
            success: false,
            error: error.message,
            fallback: true,
            ...calculateFallbackFees(totalAmount, currency)
        };
    }
};

/**
 * Fallback processing fee calculation (matches backend logic)
 * Used when API call fails
 */
const calculateFallbackFees = (totalAmount, currency) => {
    const axonalServiceFee = totalAmount * 0.01;
    const stripePercentageFee = totalAmount * 0.0109;
    const adaptiveAcceptanceFee = totalAmount * 0.0008;
    const networkTokenFee = totalAmount * 0.0012;

    const fallbackRates = {
        'USD': 1.27,
        'EUR': 1.17,
        'AED': 4.66,
        'INR': 105.85,
        'JPY': 188.50,
        'CAD': 1.72,
        'AUD': 1.92,
        'CHF': 1.12,
        'SGD': 1.72,
        'HKD': 9.92,
        'GBP': 1.00
    };

    const rate = fallbackRates[currency.toUpperCase()] || 1.27;

    const stripeFixedFee = 0.1 * rate;
    const stripe3DSecureFee = 0.03 * rate;
    const radarFraudFee = 0.06 * rate;
    const radarStandardFee = 0.04 * rate;

    const totalProcessingFees =
        axonalServiceFee +
        stripePercentageFee +
        stripeFixedFee +
        stripe3DSecureFee +
        adaptiveAcceptanceFee +
        networkTokenFee +
        radarFraudFee +
        radarStandardFee;

    const round = (num) => Math.round(num * 100) / 100;

    return {
        fees: {
            axonalServiceFee: round(axonalServiceFee),
            stripePercentageFee: round(stripePercentageFee),
            stripeFixedFee: round(stripeFixedFee),
            stripe3DSecureFee: round(stripe3DSecureFee),
            adaptiveAcceptanceFee: round(adaptiveAcceptanceFee),
            networkTokenFee: round(networkTokenFee),
            radarFraudFee: round(radarFraudFee),
            radarStandardFee: round(radarStandardFee)
        },
        totalProcessingFees: round(totalProcessingFees),
        totalWithFees: round(totalAmount + totalProcessingFees),
        currency: currency.toUpperCase(),
        fallback: true
    };
};

export default {
    calculateProcessingFees
};
